/**
 * How long each owner held the car (#585 AC4).
 *
 * SVV never states a tenure. It states when each owner's registration began,
 * so the length of owner *n*'s tenure is the gap up to owner *n-1*'s start —
 * i.e. it is derived from the NEXT-NEWER link in the chain, not from the link
 * itself. The newest owner's tenure runs to today.
 *
 * The consequence worth stating: the OLDEST link in a chain that was cut short
 * has no known end of the gap below it, so its tenure is unknowable and must
 * render as unknown rather than as zero. `eiertidForKjede` handles that, which
 * is why callers should use it instead of calling `eiertid` per row.
 *
 * The units come from leads:eiertid (#714) and are read per call, i.e. at
 * render.
 */
import i18n from '../../../i18n'

const t = i18n.getFixedT(null, 'leads')

/** A pair of ISO timestamps, oldest first. Both may be absent. */
export function eiertid(
  fra: string | null | undefined,
  til: string | null | undefined,
): string {
  if (!fra || !til) return '—'

  const start = new Date(fra)
  const slutt = new Date(til)
  if (Number.isNaN(start.getTime()) || Number.isNaN(slutt.getTime())) return '—'
  if (slutt < start) return '—'

  let maneder =
    (slutt.getFullYear() - start.getFullYear()) * 12 +
    (slutt.getMonth() - start.getMonth())
  // Not a whole month yet if the day-of-month has not come round.
  if (slutt.getDate() < start.getDate()) maneder -= 1
  if (maneder < 0) maneder = 0

  const ar = Math.floor(maneder / 12)
  const restMnd = maneder % 12

  if (ar === 0 && restMnd === 0) return t('eiertid.underOneMonth')
  if (ar === 0) return t('eiertid.months', { n: restMnd })
  if (restMnd === 0) return ar === 1 ? t('eiertid.oneYear') : t('eiertid.years', { n: ar })
  return t('eiertid.yearsMonths', { y: ar, m: restMnd })
}

interface Ledd {
  registrert_pa_eier: string | null
}

/**
 * Tenure per link, in the chain's own order (newest first).
 *
 * `avkortet` must be true when the walk stopped at `maks_steg`: we then know
 * when the oldest owner started but not that they were the first, so their
 * tenure is not '—' for lack of data but genuinely unknown. Passing false for
 * a truncated chain is how the UI would claim a complete history.
 */
export function eiertidForKjede(
  historikk: Ledd[],
  avkortet: boolean,
  na: Date = new Date(),
): string[] {
  const iDag = na.toISOString()

  return historikk.map((ledd, i) => {
    const nesteNyere = i === 0 ? iDag : historikk[i - 1].registrert_pa_eier
    const erEldste = i === historikk.length - 1
    if (erEldste && avkortet) return '—'
    return eiertid(ledd.registrert_pa_eier, nesteNyere)
  })
}
